var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = "/api/monitoring";
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"aa81d921d8f6c2b2d87485dcee48ae22feb56386"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/
import * as Sentry from '@sentry/nextjs'

import { enrichBackendRequestEvent } from './lib/api/rest/requestCorrelation'
import { trackCookieBannerTiming } from './lib/sentry/cookieBannerTiming'
import { addJourneyMeasurements } from './lib/sentry/journeyMeasurements'
import {
  annotateTransaction,
  enrichStandaloneSpans,
  dropLowValueSpans,
  getPageLoadTimingIssue,
  getTracingTags,
  tracesSampler,
} from './lib/sentry/performanceTracing'
import { tracePropagationTargets } from './lib/sentry/propagation'
import { addRouteToBaggage } from './lib/sentry/routeBaggage'
import { trackSignUpTiming } from './lib/sentry/signUpTiming'
import { isThirdPartyCallStackOverflow } from './lib/sentry/thirdPartyNoise'

const SENTRY_DSN = process.env.SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN
const environment = process.env.NEXT_PUBLIC_SENTRY_ENV || 'development'
// known issue of integration between Chrome 74.0.3729 and Sentry, which has produced 100k errors in our Sentry logs
// Open issue on Sentry is here: https://github.com/getsentry/sentry-javascript/issues/3388
const isAffectedByIssue3388 = navigator.userAgent.includes('Chrome/74.0.3729')

/**
 * @param {unknown} error
 * @returns {error is import('axios').AxiosError}
 */
const isAxiosError = (error) =>
  !!error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError === true

// Replace dynamic path segments (UUIDs, numeric IDs) with :id for consistent grouping
const normalizePath = (/** @type {string} */ pathname) =>
  pathname.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id').replace(/\/\d+/g, '/:id')

const tracingTags = getTracingTags(navigator.userAgent, window.location.search)
const pageLoadTimingIssue = getPageLoadTimingIssue()

Sentry.init({
  beforeSend(event, hint) {
    // WEB-894: Google-app injected stack overflows — see lib/sentry/thirdPartyNoise.ts
    if (isThirdPartyCallStackOverflow(event)) {
      return null
    }

    const error = hint?.originalException
    if (isAxiosError(error)) {
      const method = (error.config?.method || 'UNKNOWN').toUpperCase()
      const url = error.config?.url || 'unknown'
      const pathname = (() => {
        try {
          return normalizePath(new URL(url, 'http://placeholder').pathname)
        } catch {
          return url
        }
      })()
      const status = error.response?.status || 'network_error'

      event.fingerprint = ['axios', method, pathname, String(status)]

      event.tags = {
        ...event.tags,
        'api.method': method,
        'api.status': String(status),
        'api.endpoint': pathname,
      }

      // Override exception title so Sentry issues and alerts show the endpoint, not generic "AxiosError"
      if (event.exception?.values?.[0]) {
        event.exception.values[0].type = `AxiosError [${method} ${pathname}]`
        event.exception.values[0].value = `${status} — ${error.message}`
      }
    }
    return enrichBackendRequestEvent(event, error)
  },
  integrations: [
    // Replaces the default added once tracing is on: INP is opt-in on v7, and
    // long-task spans would add volume without answering "why is it slow".
    // The class, not browserTracingIntegration(): on v7 @sentry/nextjs rebuilds
    // the function form, and the rebuilt copy never finalises LCP.
    // oxlint-disable-next-line import/namespace -- exported by the browser entry; oxlint resolves the server one
    new Sentry.BrowserTracing({ enableInp: true, enableLongTask: false }),
    new Sentry.Integrations.TryCatch({
      requestAnimationFrame: !isAffectedByIssue3388,
      eventTarget: !isAffectedByIssue3388,
      // @ts-expect-error — `rewriteFrames` doesn't exist?
      rewriteFrames: {
        iteratee: (/** @type {import('@sentry/types').StackFrame} */ frame) => {
          if (frame.filename && frame.filename.startsWith('app://')) {
            frame.filename = frame.filename.replace('app://', '')
          }
          return frame
        },
      },
    }),
  ],
  ignoreErrors: [
    // Non-actionable error messages
    'Network Error',
    'GEO-IP lookup: Call to getipcountry.zoe.net failed with Network Error',
    'GEO-IP lookup: Call to getipcountry.zoe.net failed with 403',
    // Ignores spammy errors that seem to be caused by problematic integration between Next.js and Sentry (https://github.com/getsentry/sentry-javascript/issues/3440)
    'Non-Error promise rejection captured',
    'Hydration failed because the initial UI does not match what was rendered on the server.',
    'There was an error while hydrating. Because the error happened outside of a Suspense boundary, the entire root will switch to client rendering.',
    'Text content does not match server-rendered HTML.',
    'This Suspense boundary received an update before it finished hydrating. This caused the boundary to switch to client rendering. The usual way to fix this is to wrap the original update in startTransition',
    /Minified React error #(\d+); visit https:\/\/reactjs\.org\/docs\/error-decoder\.html\?invariant=\d+ for the full message or use the non-minified dev environment for full errors and additional helpful warnings\./,
    // GTM/GA errors
    /Can't find variable: (_tfa|twq|ttq|PaymentAutofillConfig)/,
    /(_tfa|twq|ttq) is not defined/,
    // M&S tracking errors
    'mnsInlineData',
    // Browser / browser extension errors
    'Extension context invalidated.',
    /.*@webkit-masked-url.*/,
    /window\._?webkit(Namespace)?\.messageHandlers/,
    // WEB-1378: same bridge call from iOS in-app browsers, but obfuscated (window[tn(0xa2b)]...)
    /evaluating 'window\[\w+\(0x[0-9a-f]+\)\]/,
    '_chromeNamespace',
    // onetrust / cookiehub errors
    "TypeError: undefined is not an object (evaluating 't.Domain')",
    'SyntaxError: Unexpected token \'j\', "jsonFeed({"... is not valid JSON',
    "TypeError: undefined is not an object (evaluating 'r.DomainData')",
    /cookiehub/i,
    'CustomEvent: Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
    'Event `XMLHttpRequestProgressEvent` (type=error) captured as promise rejection',
    'Event `ProgressEvent` (type=error) captured as promise rejection',
    // Tiktok browser error
    "Cannot read properties of undefined (reading 'domInteractive')",
    // False positive on navigation to the same URL
    /Invariant: attempted to hard navigate to the same URL.*/,
    // GMO errors in iphones, known issue by google https://issuetracker.google.com/issues/396043331?pli=1
    "Can't find variable: gmo",
    // WEB-1279: bridge errors from the Meta in-app browser's injected script
    /^Error invoking postMessage: /,
    // WEB-1107: Optimizely snippet rejects its event/datafile XHR with the raw
    // XMLHttpRequest when the request is blocked (ad-blockers, flaky networks).
    // Frameless unhandled rejection, so denyUrls can't catch it; anchored so a
    // real error merely containing the string is never masked.
    /^\[object XMLHttpRequest\]$/,
  ],
  denyUrls: [
    // WEB-1107: Optimizely snippet internals (e.g. CookieSetError on
    // optimizelyEndUserId when cookies are blocked) — not actionable by us.
    // Both forms: frames record app:///js/<id>.js after rewriteFrames.
    /\/js\/6549461774630912\.js/,
    /cdn\.optimizely\.com/,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Ignore Google flakiness
    /\/(gtm|ga|analytics)\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    // Firefox extensions
    /^resource:\/\//i,
    // Other plugins
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    /cdn\.cookielaw\.org/i,
    /cookiehub\.net/i,
    /scripttemplates\//i,
  ],
  dsn: SENTRY_DSN,
  // Product pages and checkout only — see lib/sentry/performanceTracing.ts
  tracesSampler,
  // Charts filter on timing_quality:ok — see annotateTransaction in lib/sentry/performanceTracing.ts
  beforeSendTransaction: (event) => {
    // Before annotating, so the journey step spans get the tracing tags and timing_quality too.
    addJourneyMeasurements(dropLowValueSpans(event))
    return annotateTransaction(event, tracingTags, pageLoadTimingIssue)
  },
  tracePropagationTargets,
  environment: environment,
  initialScope: { tags: tracingTags },
  // INP spans bypass the scope on v7, so they get the same values as attributes
  // here, plus the sampling context Sentry's ingest requires for standalone spans
  transport: (/** @type {Parameters<typeof Sentry.makeFetchTransport>[0]} */ options) => {
    // oxlint-disable-next-line import/namespace -- exported by the browser entry; oxlint resolves the server one
    const transport = Sentry.makeFetchTransport(options)
    return {
      ...transport,
      send: (/** @type {import('@sentry/types').Envelope} */ envelope) => {
        const client = Sentry.getClient()
        const publicKey = client?.getDsn()?.publicKey
        const samplingContext = publicKey
          ? { public_key: publicKey, environment, release: client?.getOptions().release }
          : undefined
        return transport.send(enrichStandaloneSpans(envelope, tracingTags, samplingContext))
      },
    }
  },
  // ...
  // Note: if you want to override the automatic release value, do not set a
  // `release` value here - use the environment variable `SENTRY_RELEASE`, so
  // that it will also get attached to your source maps
})

Sentry.getClient()?.on?.('createDsc', addRouteToBaggage)

trackCookieBannerTiming()
trackSignUpTiming()
